import type { EffectInstance, EffectType, WamParameterInfo } from '@/domain/types';
import { withTrack } from '@/domain/mutate';
import { EFFECT_DESCRIPTORS, defaultParams, effectParamDescriptor } from '@/engine/effects/descriptors';
import { isWamUrl } from '@/engine/effects/wamHost';
import { readWamState } from '@/domain/format/parse';
import { newEffectId } from '@/util/id';
import { defineCommand } from './registry';
import { boolParam, enumParam, idParam, intParam, numberParam, stringParam } from './params';

const EFFECT_TYPES = Object.keys(EFFECT_DESCRIPTORS) as EffectType[];

export const addEffect = defineCommand<{
  trackId: string;
  effectId: string;
  type: EffectType;
  index: number | null;
  /** For a `wam` insert: where the plugin's code comes from. Required for one, meaningless for the rest. */
  wamUrl: string | null;
}>({
  type: 'effect.add',
  label: (p) => `Add ${EFFECT_DESCRIPTORS[p.type].label}`,
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const type = enumParam(raw.type, EFFECT_TYPES);
    const effectId = raw.effectId === undefined ? newEffectId() : idParam(raw.effectId);
    if (!trackId || !type || !effectId) return null;
    const wamUrl = typeof raw.wamUrl === 'string' && isWamUrl(raw.wamUrl) ? raw.wamUrl : null;
    if (type === 'wam' && !wamUrl) return null;
    return { trackId, type, effectId, index: raw.index === undefined ? null : intParam(raw.index, 0, 64), wamUrl: type === 'wam' ? wamUrl : null };
  },
  apply: (project, p) =>
    withTrack(project, p.trackId, (track) => {
      // A long insert chain is almost always a mistake rather than an intent,
      // and every insert costs CPU on every buffer.
      if (track.effects.length >= 16) return track;
      if (track.effects.some((e) => e.id === p.effectId)) return track;
      const effect: EffectInstance = { id: p.effectId, type: p.type, enabled: true, params: defaultParams(p.type) };
      if (p.wamUrl) {
        effect.wamUrl = p.wamUrl;
        effect.wamName = 'Web Audio Module';
        effect.wamParameters = [];
      }
      const effects = [...track.effects];
      effects.splice(p.index ?? effects.length, 0, effect);
      return { ...track, effects };
    }),
});

/**
 * What a hosted plugin said about itself once it loaded: its name and its
 * parameters. Written by the engine through the store, so a project carries
 * enough to draw the plugin's controls before — or without — the plugin.
 * Parameters the plugin no longer has keep their values in `params`, harmless
 * and invisible; a parameter it gained starts at its default.
 */
export const setWamInfo = defineCommand<{ trackId: string; effectId: string; name: string; parameters: WamParameterInfo[] }>({
  type: 'effect.setWamInfo',
  label: () => 'Plugin loaded',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const effectId = idParam(raw.effectId);
    const name = stringParam(raw.name, 128);
    if (!trackId || !effectId || !name || !Array.isArray(raw.parameters)) return null;
    const parameters: WamParameterInfo[] = [];
    for (const entry of raw.parameters.slice(0, 256)) {
      if (typeof entry !== 'object' || entry === null) return null;
      const e = entry as Record<string, unknown>;
      const id = stringParam(e.id, 64);
      const min = numberParam(e.min, -1e9, 1e9);
      const max = numberParam(e.max, -1e9, 1e9);
      if (!id || min === null || max === null || !(max > min)) return null;
      parameters.push({
        id,
        label: stringParam(e.label, 64) ?? id,
        min,
        max,
        default: numberParam(e.default, min, max, min) ?? min,
        units: stringParam(e.units, 16) ?? '',
      });
    }
    return { trackId, effectId, name, parameters };
  },
  apply: (project, p) => {
    const target = project.tracks[p.trackId]?.effects.find((e) => e.id === p.effectId);
    if (!target || target.type !== 'wam') return project;
    const same =
      target.wamName === p.name && JSON.stringify(target.wamParameters ?? []) === JSON.stringify(p.parameters);
    if (same) return project;
    return withTrack(project, p.trackId, (track) => ({
      ...track,
      effects: track.effects.map((effect) => {
        if (effect.id !== p.effectId) return effect;
        const params = { ...effect.params };
        for (const parameter of p.parameters) if (params[parameter.id] === undefined) params[parameter.id] = parameter.default;
        return { ...effect, wamName: p.name, wamParameters: p.parameters, params };
      }),
    }));
  },
});

/**
 * Gives a reverb a room: the asset whose audio is its impulse response, or
 * null to go back to the synthesised tail. Refused on anything but a reverb,
 * and on an asset the project does not hold.
 */
export const setEffectImpulse = defineCommand<{ trackId: string; effectId: string; assetId: string | null }>({
  type: 'effect.setImpulse',
  label: (p) => (p.assetId ? 'Use a recorded room' : 'Use the synthesised reverb'),
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const effectId = idParam(raw.effectId);
    const assetId = raw.assetId === null ? null : idParam(raw.assetId);
    if (!trackId || !effectId || (raw.assetId !== null && !assetId)) return null;
    return { trackId, effectId, assetId };
  },
  apply: (project, p) => {
    if (p.assetId !== null && !project.assets[p.assetId]) return project;
    const target = project.tracks[p.trackId]?.effects.find((e) => e.id === p.effectId);
    if (!target || target.type !== 'reverb' || (target.impulseAssetId ?? null) === p.assetId) return project;
    return withTrack(project, p.trackId, (track) => ({
      ...track,
      effects: track.effects.map((effect) => {
        if (effect.id !== p.effectId || effect.type !== 'reverb') return effect;
        if ((effect.impulseAssetId ?? null) === p.assetId) return effect;
        const rest: EffectInstance = { id: effect.id, type: effect.type, enabled: effect.enabled, params: effect.params };
        return p.assetId === null ? rest : { ...rest, impulseAssetId: p.assetId };
      }),
    }));
  },
});

export const removeEffect = defineCommand<{ trackId: string; effectId: string }>({
  type: 'effect.remove',
  label: () => 'Remove effect',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const effectId = idParam(raw.effectId);
    return trackId && effectId ? { trackId, effectId } : null;
  },
  apply: (project, p) => {
    // Lanes that pointed at this effect are removed with it: a lane whose
    // target no longer exists would draw a curve that controls nothing.
    const automation = { ...project.automation };
    const orphaned = new Set<string>();
    for (const lane of Object.values(automation)) {
      if (lane.target.kind === 'effect' && lane.target.effectId === p.effectId) {
        orphaned.add(lane.id);
        delete automation[lane.id];
      }
    }

    return withTrack({ ...project, automation }, p.trackId, (track) => ({
      ...track,
      effects: track.effects.filter((e) => e.id !== p.effectId),
      automationLaneIds: track.automationLaneIds.filter((id) => !orphaned.has(id)),
    }));
  },
});

export const setEffectEnabled = defineCommand<{ trackId: string; effectId: string; enabled: boolean }>({
  type: 'effect.setEnabled',
  label: (p) => (p.enabled ? 'Enable effect' : 'Bypass effect'),
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const effectId = idParam(raw.effectId);
    const enabled = boolParam(raw.enabled);
    return trackId && effectId && enabled !== null ? { trackId, effectId, enabled } : null;
  },
  apply: (project, p) =>
    withTrack(project, p.trackId, (track) => ({
      ...track,
      effects: track.effects.map((e) => (e.id === p.effectId ? { ...e, enabled: p.enabled } : e)),
    })),
});

export const setEffectParam = defineCommand<{ trackId: string; effectId: string; paramId: string; value: number }>({
  type: 'effect.setParam',
  label: () => 'Adjust effect',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const effectId = idParam(raw.effectId);
    const paramId = stringParam(raw.paramId, 64);
    const value = numberParam(raw.value, -1e6, 1e6);
    return trackId && effectId && paramId && value !== null ? { trackId, effectId, paramId, value } : null;
  },
  coalesceKey: (p) => `fxParam:${p.effectId}:${p.paramId}`,
  apply: (project, p) =>
    withTrack(project, p.trackId, (track) => ({
      ...track,
      effects: track.effects.map((effect) => {
        if (effect.id !== p.effectId) return effect;
        // Clamping happens against the effect's own descriptor, so a value from
        // a script or an older file can never drive a node out of range.
        const descriptor = effectParamDescriptor(effect, p.paramId);
        if (!descriptor) return effect;
        const value = Math.min(descriptor.max, Math.max(descriptor.min, p.value));
        return { ...effect, params: { ...effect.params, [p.paramId]: value } };
      }),
    })),
});

export const reorderEffect = defineCommand<{ trackId: string; effectId: string; toIndex: number }>({
  type: 'effect.reorder',
  label: () => 'Reorder effects',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const effectId = idParam(raw.effectId);
    const toIndex = intParam(raw.toIndex, 0, 64);
    return trackId && effectId && toIndex !== null ? { trackId, effectId, toIndex } : null;
  },
  apply: (project, p) =>
    withTrack(project, p.trackId, (track) => {
      const from = track.effects.findIndex((e) => e.id === p.effectId);
      if (from < 0) return track;
      const effects = [...track.effects];
      const [moved] = effects.splice(from, 1);
      effects.splice(Math.min(p.toIndex, effects.length), 0, moved);
      return { ...track, effects };
    }),
});

/**
 * The plugin's whole state, as it reported it. Written by the engine through
 * the store when the project is saved, packaged, or the plugin's interface
 * is closed; not an edit, so it never dirties the project or makes an undo
 * step on its own. Any JSON the plugin gave is kept as it is.
 */
export const setWamState = defineCommand<{ trackId: string; effectId: string; state: unknown }>({
  type: 'effect.setWamState',
  label: () => 'Plugin state',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const effectId = idParam(raw.effectId);
    if (!trackId || !effectId) return null;
    const state = readWamState(raw.state);
    if (state === undefined) return null;
    return { trackId, effectId, state };
  },
  apply: (project, p) => {
    const target = project.tracks[p.trackId]?.effects.find((e) => e.id === p.effectId);
    if (!target || target.type !== 'wam') return project;
    if (JSON.stringify(target.wamState ?? null) === JSON.stringify(p.state)) return project;
    return withTrack(project, p.trackId, (track) => ({
      ...track,
      effects: track.effects.map((effect) => (effect.id === p.effectId ? { ...effect, wamState: p.state } : effect)),
    }));
  },
});
